'use client';

import { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip';
import type { ReactElement, ReactNode } from 'react';

import { cn } from './utils';

/**
 * Hover and focus hint, on @base-ui/react's Tooltip — in place of the native
 * `title`, which the browser only shows after a second of holding still and
 * drops if the pointer leaves first.
 *
 * Opens quickly (the delay only has to filter out a pointer passing
 * through), and inside a TooltipProvider the next tooltip opens at once
 * while one is showing, so running along a toolbar reads each button.
 *
 * Wraps a single element and attaches to it rather than adding a wrapper, so
 * the child keeps its own accessible name — the hint is extra, never the
 * only label.
 */
function TooltipProvider({
  closeDelay = 0,
  delay = 120,
  ...props
}: TooltipPrimitive.Provider.Props) {
  return (
    <TooltipPrimitive.Provider
      closeDelay={closeDelay}
      delay={delay}
      {...props}
    />
  );
}

function Tooltip({
  align = 'center',
  children,
  className,
  delay = 120,
  label,
  side = 'top',
  sideOffset = 6,
}: {
  /** The element the hint is for, rendered as the trigger itself. */
  children: ReactElement;
  className?: string;
  /** Milliseconds before it opens on hover. Focus opens it at once. */
  delay?: number;
  /** Nothing, and the child renders with no tooltip at all. */
  label: ReactNode;
} & Pick<TooltipPrimitive.Positioner.Props, 'align' | 'side' | 'sideOffset'>) {
  if (label === undefined || label === null || label === '') return children;
  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger delay={delay} render={children} />
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Positioner
          align={align}
          className="z-50"
          side={side}
          sideOffset={sideOffset}
        >
          <TooltipPrimitive.Popup
            className={cn(
              'max-w-64 origin-(--transform-origin) rounded-md bg-foreground px-2 py-1 text-xs leading-snug text-background shadow-md transition-[opacity,transform] duration-100 data-ending-style:scale-95 data-ending-style:opacity-0 data-instant:duration-0 data-starting-style:scale-95 data-starting-style:opacity-0',
              className,
            )}
            data-slot="tooltip-content"
          >
            {label}
          </TooltipPrimitive.Popup>
        </TooltipPrimitive.Positioner>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

export { Tooltip, TooltipProvider };
